import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Images, Copy, Undo2, Loader2, CheckSquare, Square, X } from 'lucide-react';
import { Modal } from '../UI/Modal';
import { TabBar, TabItem } from '../UI/TabBar';
import { PhotoCardSkeleton } from '../UI/Skeleton';
import { AlbumPhotoCell } from './AlbumPhotoCell';
import { AlbumPhotoViewer } from './AlbumPhotoViewer';
import { MachhaCharacter } from '../../lib/machhaAsset';
import { albumsApi } from '../../api/albums';
import { Album, Photo } from '../../types';
import styles from './AlbumPhotosPreviewModal.module.css';

type PreviewTab = 'all-photos' | 'duplicates';

const TABS: TabItem<PreviewTab>[] = [
  { id: 'all-photos', label: 'All Photos', icon: Images },
  { id: 'duplicates', label: 'Duplicate Rejected', icon: Copy },
];

interface AlbumPhotosPreviewModalProps {
  album: Album;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Creator-side modal that shows all album photos with overlays composited,
 * using the same AlbumPhotoCell grid as the joined member view.
 */
export function AlbumPhotosPreviewModal({ album, isOpen, onClose }: AlbumPhotosPreviewModalProps) {
  const [activeTab, setActiveTab] = useState<PreviewTab>('all-photos');

  // ── All Photos tab state ─────────────────────────────────────────────────
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const cursorRef = useRef<string | null>(null);
  const loadingMoreRef = useRef(false);

  // ── Duplicates tab state ─────────────────────────────────────────────────
  const [rejectedPhotos, setRejectedPhotos] = useState<Photo[]>([]);
  const [rejectedHasMore, setRejectedHasMore] = useState(false);
  const [rejectedLoading, setRejectedLoading] = useState(false);
  const [rejectedLoadingMore, setRejectedLoadingMore] = useState(false);
  const [rejectedInitialized, setRejectedInitialized] = useState(false);
  const rejectedCursorRef = useRef<string | null>(null);
  const rejectedLoadingMoreRef = useRef(false);

  // ── Selection state (duplicates tab) ────────────────────────────────────
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRestoring, setBulkRestoring] = useState(false);

  // ── Single-restore state ─────────────────────────────────────────────────
  const [restoringIds, setRestoringIds] = useState<Set<string>>(new Set());

  // ── Shared viewer state ──────────────────────────────────────────────────
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  // ── Infinite-scroll observers ────────────────────────────────────────────
  const observerRef = useRef<IntersectionObserver | null>(null);
  const rejectedObserverRef = useRef<IntersectionObserver | null>(null);

  // Load all-photos on open
  useEffect(() => {
    if (!isOpen || initialized) return;
    setLoading(true);
    albumsApi
      .listPhotos(album._id)
      .then((r) => {
        setPhotos(r.photos);
        cursorRef.current = r.nextCursor;
        setHasMore(r.hasMore);
        setInitialized(true);
      })
      .catch(() => toast.error('Failed to load photos'))
      .finally(() => setLoading(false));
  }, [isOpen, initialized, album._id]);

  // Load duplicates when that tab is first opened
  useEffect(() => {
    if (!isOpen || activeTab !== 'duplicates' || rejectedInitialized) return;
    setRejectedLoading(true);
    albumsApi
      .listRejectedPhotos(album._id)
      .then((r) => {
        setRejectedPhotos(r.photos);
        rejectedCursorRef.current = r.nextCursor;
        setRejectedHasMore(r.hasMore);
        setRejectedInitialized(true);
      })
      .catch(() => toast.error('Failed to load rejected photos'))
      .finally(() => setRejectedLoading(false));
  }, [isOpen, activeTab, rejectedInitialized, album._id]);

  // Reset everything when closed
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('all-photos');
      setInitialized(false);
      setPhotos([]);
      setHasMore(false);
      cursorRef.current = null;
      loadingMoreRef.current = false;
      setRejectedInitialized(false);
      setRejectedPhotos([]);
      setRejectedHasMore(false);
      rejectedCursorRef.current = null;
      rejectedLoadingMoreRef.current = false;
      setViewerIndex(null);
      setSelectMode(false);
      setSelectedIds(new Set());
    }
  }, [isOpen]);

  // Exit select mode when switching tabs
  useEffect(() => {
    setSelectMode(false);
    setSelectedIds(new Set());
  }, [activeTab]);

  const loadMore = useCallback(async () => {
    const cursor = cursorRef.current;
    if (!cursor || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const r = await albumsApi.listPhotos(album._id, cursor);
      setPhotos((prev) => [...prev, ...r.photos]);
      cursorRef.current = r.nextCursor;
      setHasMore(r.hasMore);
    } catch {
      toast.error('Failed to load more photos');
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [album._id]);

  const loadMoreRejected = useCallback(async () => {
    const cursor = rejectedCursorRef.current;
    if (!cursor || rejectedLoadingMoreRef.current) return;
    rejectedLoadingMoreRef.current = true;
    setRejectedLoadingMore(true);
    try {
      const r = await albumsApi.listRejectedPhotos(album._id, cursor);
      setRejectedPhotos((prev) => [...prev, ...r.photos]);
      rejectedCursorRef.current = r.nextCursor;
      setRejectedHasMore(r.hasMore);
    } catch {
      toast.error('Failed to load more rejected photos');
    } finally {
      rejectedLoadingMoreRef.current = false;
      setRejectedLoadingMore(false);
    }
  }, [album._id]);

  const loadMoreFnRef = useRef(loadMore);
  useEffect(() => { loadMoreFnRef.current = loadMore; }, [loadMore]);

  const loadMoreRejectedFnRef = useRef(loadMoreRejected);
  useEffect(() => { loadMoreRejectedFnRef.current = loadMoreRejected; }, [loadMoreRejected]);

  const sentinelCallbackRef = useCallback((el: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el) return;
    observerRef.current = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMoreFnRef.current(); },
      { rootMargin: '300px' },
    );
    observerRef.current.observe(el);
  }, []);

  const rejectedSentinelCallbackRef = useCallback((el: HTMLDivElement | null) => {
    rejectedObserverRef.current?.disconnect();
    rejectedObserverRef.current = null;
    if (!el) return;
    rejectedObserverRef.current = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMoreRejectedFnRef.current(); },
      { rootMargin: '300px' },
    );
    rejectedObserverRef.current.observe(el);
  }, []);

  const handleRestore = useCallback(async (photoId: string) => {
    setRestoringIds((prev) => new Set(prev).add(photoId));
    try {
      await albumsApi.restoreRejectedPhoto(album._id, photoId);
      setRejectedPhotos((prev) => prev.filter((p) => p._id !== photoId));
      toast.success('Photo moved to album');
    } catch {
      toast.error('Failed to restore photo');
    } finally {
      setRestoringIds((prev) => { const s = new Set(prev); s.delete(photoId); return s; });
    }
  }, [album._id]);

  const handleToggleSelect = useCallback((photoId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(photoId)) next.delete(photoId);
      else next.add(photoId);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds(new Set(rejectedPhotos.map((p) => p._id)));
  }, [rejectedPhotos]);

  const handleDeselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleBulkRestore = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkRestoring(true);
    try {
      await albumsApi.bulkRestoreRejectedPhotos(album._id, ids);
      setRejectedPhotos((prev) => prev.filter((p) => !selectedIds.has(p._id)));
      setSelectedIds(new Set());
      setSelectMode(false);
      toast.success(`${ids.length} photo${ids.length !== 1 ? 's' : ''} moved to album`);
    } catch {
      toast.error('Failed to move photos to album');
    } finally {
      setBulkRestoring(false);
    }
  }, [album._id, selectedIds]);

  const allSelected = rejectedPhotos.length > 0 && selectedIds.size === rejectedPhotos.length;

  const viewerPhotos = activeTab === 'duplicates' ? rejectedPhotos : photos;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={album.name} size="full">
        <div className={styles['content']}>
          {album.description && (
            <p className={styles['description']}>
              {album.description}
            </p>
          )}

          <TabBar
            tabs={TABS}
            activeTab={activeTab}
            onChange={setActiveTab}
            layoutId="album-preview-tab"
            variant="underline"
          />

          {/* ── All Photos tab ─────────────────────────────────────── */}
          {activeTab === 'all-photos' && (
            loading ? (
              <div className={styles['photo-grid']}>
                {Array.from({ length: 10 }).map((_, i) => <PhotoCardSkeleton key={i} />)}
              </div>
            ) : photos.length === 0 ? (
              <div className={styles['empty-state']}>
                <MachhaCharacter state="Empty" size={100} />
                <p className={styles['empty-text']}>No photos in this album yet</p>
              </div>
            ) : (
              <>
                <div className={styles['photo-grid']}>
                  {photos.map((photo, i) => (
                    <AlbumPhotoCell
                      key={photo._id}
                      photo={photo}
                      photoIndex={i}
                      isSelected={false}
                      selectMode={false}
                      onSelect={() => {}}
                      onView={setViewerIndex}
                    />
                  ))}
                </div>
                {hasMore && <div ref={sentinelCallbackRef} className={styles['sentinel']} />}
                {loadingMore && (
                  <div className={styles['loading-more-grid']}>
                    {Array.from({ length: 5 }).map((_, i) => <PhotoCardSkeleton key={i} />)}
                  </div>
                )}
                {!hasMore && (
                  <p className={styles['total-label']}>
                    — {photos.length} photo{photos.length !== 1 ? 's' : ''} total —
                  </p>
                )}
              </>
            )
          )}

          {/* ── Duplicate Rejected tab ─────────────────────────────── */}
          {activeTab === 'duplicates' && (
            rejectedLoading ? (
              <div className={styles['photo-grid']}>
                {Array.from({ length: 10 }).map((_, i) => <PhotoCardSkeleton key={i} />)}
              </div>
            ) : rejectedPhotos.length === 0 ? (
              <div className={styles['empty-state']}>
                <MachhaCharacter state="Success" size={100} />
                <p className={styles['empty-text']}>No duplicate-rejected photos</p>
              </div>
            ) : (
              <>
                <p className={styles['rejected-note']}>
                  These photos were rejected from the live album because a visually similar photo was already added.
                </p>

                {/* Selection toolbar */}
                <div className={styles['select-toolbar']}>
                  {!selectMode ? (
                    <button
                      className={styles['select-toggle-btn']}
                      onClick={() => setSelectMode(true)}
                    >
                      <CheckSquare size={14} />
                      Select
                    </button>
                  ) : (
                    <>
                      <button
                        className={styles['select-toggle-btn']}
                        onClick={allSelected ? handleDeselectAll : handleSelectAll}
                      >
                        {allSelected
                          ? <><CheckSquare size={14} />Deselect all</>
                          : <><Square size={14} />Select all</>
                        }
                      </button>
                      <button
                        className={styles['select-cancel-btn']}
                        onClick={() => { setSelectMode(false); setSelectedIds(new Set()); }}
                      >
                        <X size={13} />
                        Cancel
                      </button>
                      {selectedIds.size > 0 && (
                        <button
                          className={styles['bulk-restore-btn']}
                          onClick={handleBulkRestore}
                          disabled={bulkRestoring}
                        >
                          {bulkRestoring
                            ? <><Loader2 size={13} className={styles['restore-btn-spinner']} />Moving…</>
                            : <><Undo2 size={13} />Move {selectedIds.size} to album</>
                          }
                        </button>
                      )}
                    </>
                  )}
                </div>

                <div className={styles['photo-grid']}>
                  {rejectedPhotos.map((photo, i) => (
                    <div key={photo._id} className={styles['restore-cell']}>
                      <AlbumPhotoCell
                        photo={photo}
                        photoIndex={i}
                        isSelected={selectedIds.has(photo._id)}
                        selectMode={selectMode}
                        onSelect={handleToggleSelect}
                        onView={selectMode ? () => {} : setViewerIndex}
                      />
                      {!selectMode && (
                        <button
                          className={styles['restore-btn']}
                          onClick={(e) => { e.stopPropagation(); handleRestore(photo._id); }}
                          disabled={restoringIds.has(photo._id)}
                          title="Move to album"
                        >
                          {restoringIds.has(photo._id)
                            ? <Loader2 size={13} className={styles['restore-btn-spinner']} />
                            : <Undo2 size={13} />
                          }
                          <span>{restoringIds.has(photo._id) ? 'Moving…' : 'Move to album'}</span>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {rejectedHasMore && <div ref={rejectedSentinelCallbackRef} className={styles['sentinel']} />}
                {rejectedLoadingMore && (
                  <div className={styles['loading-more-grid']}>
                    {Array.from({ length: 5 }).map((_, i) => <PhotoCardSkeleton key={i} />)}
                  </div>
                )}
                {!rejectedHasMore && (
                  <p className={styles['total-label']}>
                    — {rejectedPhotos.length} rejected photo{rejectedPhotos.length !== 1 ? 's' : ''} —
                  </p>
                )}
              </>
            )
          )}
        </div>
      </Modal>

      {viewerIndex !== null && (
        <AlbumPhotoViewer
          photos={viewerPhotos}
          initialIndex={viewerIndex}
          onClose={() => setViewerIndex(null)}
        />
      )}
    </>
  );
}
